import { useEffect, useState } from "react";
import { useBlocker, useNavigate } from "react-router-dom";
import { useWatch } from "react-hook-form";
import { Paper } from "@mantine/core";
import useManualEntryStore from "../../../stores/manualEntryStore";
import { useManualEntryForm } from "./validations/useManualEntryForm";
import { useManualEntry } from "./hooks/useManualEntry";
import { useGetDisbursementLimits } from "../../../hooks/queries/useBulkUploadsQueries";
import { usePhoneVerification } from "./hooks/usePhoneVerification";
import { useBankVerification } from "./hooks/useBankVerification";
import RecipientCard from "./components/RecipientCard";
import BatchSummaryCard from "./components/BatchSummaryCard";
import ManualEntryTable from "./components/ManualEntryTable";
import ManualEntrySuccessModal from "./components/ManualEntrySuccessModal";
import CustomButton from "../../ui/CustomButton/CustomButton";
import { CustomLoadingOverlay } from "../../ui/CustomLoadingOverlay/CustomLoadingOverLay";
import CustomLeaveConfirmationModal from "../../ui/CustomLeaveConfirmationModal/CustomLeaveConfirmationModal";
import { MANUAL_ENTRY_CHANNELS } from "./config/manualEntryConfig";

function ManualEntryPage() {
  const navigate = useNavigate();
  const [successData, setSuccessData] = useState(null);

  const { control, handleSubmit, reset, setValue, validators } = useManualEntryForm();

  const [selectedChannel, phoneValue, watchedBankCode, watchedAccountNumber, recipientName, watchedAmount, watchedRemarks] = useWatch({
    control,
    name: ["channel", "phoneNumber", "bankCode", "accountNumber", "recipientName", "amount", "remarks"],
  });

  const {
    walletBalance,
    isBalanceError,
    totalDisbursement,
    totalCharges,
    totalPlusFees,
    editingEntryTotalCost,
    totalRecipients,
    entries,
    isAtLimit,
    isChannelLocked,
    isSubmitting,
    isSubmittingBatch,
    editingId,
    paymentChannel,
    handleAddToList,
    handleUpdateEntry,
    handleSubmitBatch,
    banks,
    bankOptions,
    bankFilter,
    isBanksLoading,
    isBanksError,
    refetchBanks,
  } = useManualEntry(selectedChannel === MANUAL_ENTRY_CHANNELS.BANK);

  const { data: disbursementLimits, isLoading: isLimitsLoading } =
    useGetDisbursementLimits(selectedChannel);

  const isBank = selectedChannel === MANUAL_ENTRY_CHANNELS.BANK;

  const isFormValid = isBank
    ? !validators.channel(selectedChannel) &&
      !validators.bankCode(watchedBankCode) &&
      !validators.accountNumber(watchedAccountNumber) &&
      !validators.recipientName(recipientName) &&
      !validators.amount(watchedAmount, isBank) &&
      !validators.remarks(watchedRemarks, isBank)
    : !validators.channel(selectedChannel) &&
      !validators.phoneNumber(phoneValue) &&
      !validators.recipientName(recipientName) &&
      !validators.amount(watchedAmount, isBank) &&
      !validators.remarks(watchedRemarks, isBank);

  const { handlePhoneChange, handlePhoneBlur, isVerifying, isVerified, isUnverified } =
    usePhoneVerification({ paymentChannel: selectedChannel || null, phoneValue, setValue });

  const {
    handleAccountNumberChange,
    handleAccountNumberBlur,
    isVerifying: isBankVerifying,
    isVerified: isBankVerified,
    isUnverified: isBankUnverified,
  } = useBankVerification({
    paymentChannel: selectedChannel || null,
    accountNumber: watchedAccountNumber,
    bankCode: watchedBankCode,
    setValue,
  });

  const isEditing = !!editingId;

  const editingEntryAmount = entries.find((e) => e.id === editingId)?.amount ?? 0;
  const committedDisbursement = isEditing
    ? totalDisbursement - editingEntryAmount
    : totalDisbursement;
  const isLimitExceeded =
    disbursementLimits?.availableDailyLimit != null &&
    Number(watchedAmount) + committedDisbursement > disbursementLimits.availableDailyLimit;

  const shouldGuardLeave = entries.length > 0;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      entries.length > 0 && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!shouldGuardLeave) return;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldGuardLeave]);

  const handleEditEntry = (rowData) => {
    const editingAsBank = paymentChannel === MANUAL_ENTRY_CHANNELS.BANK;
    useManualEntryStore.getState().setEditing(
      rowData.id,
      editingAsBank
        ? { accountNumber: rowData.accountNumber, bankCode: rowData.bankCode }
        : { phone: rowData.phoneNumber, name: rowData.recipientName },
    );
    reset(
      editingAsBank
        ? {
            recipientName: rowData.recipientName,
            phoneNumber: "",
            bankCode: rowData.bankCode ?? "",
            accountNumber: rowData.accountNumber ?? "",
            amount: rowData.amount,
            remarks: rowData.remarks,
            channel: paymentChannel ?? "",
          }
        : {
            recipientName: rowData.recipientName,
            phoneNumber: rowData.phoneNumber ?? "",
            bankCode: "",
            accountNumber: "",
            amount: rowData.amount,
            remarks: rowData.remarks,
            channel: paymentChannel ?? "",
          },
    );
  };

  const handleCancelEdit = () => {
    useManualEntryStore.getState().clearEditing();
    reset({
      recipientName: "",
      phoneNumber: "",
      bankCode: "",
      accountNumber: "",
      amount: "",
      remarks: "",
      channel: paymentChannel ?? "",
    });
  };

  return (
    <>
      <CustomLoadingOverlay
        fullScreen
        visible={isSubmittingBatch}
        overlayProps={{ blur: 1.4, backgroundOpacity: 0.15 }}
        loaderProps={{ color: "primary", type: "bars" }}
      />
      <CustomLeaveConfirmationModal
        opened={blocker.state === "blocked"}
        title="Leave and discard entries?"
        message={
          <>
            You have{" "}
            <span className="font-medium text-gray-700">
              {entries.length} {entries.length === 1 ? "entry" : "entries"}
            </span>{" "}
            that haven&apos;t been submitted. Leaving now will discard all
            progress.
          </>
        }
        leaveLabel="Leave and discard"
        onStay={() => blocker.reset()}
        onLeave={() => blocker.proceed()}
      />
      <div className="flex-1 p-4 sm:p-6">
        <div className="flex flex-col md:flex-row gap-4 items-start">
          <RecipientCard
            control={control}
            validators={validators}
            onAddToList={handleSubmit((data) =>
              isEditing
                ? handleUpdateEntry(data, reset)
                : handleAddToList(data, reset),
            )}
            walletBalance={walletBalance}
            totalPlusFees={totalPlusFees}
            editingEntryTotalCost={editingEntryTotalCost}
            isSubmitting={isSubmitting}
            isAtLimit={isAtLimit}
            isEditing={isEditing}
            onCancelEdit={handleCancelEdit}
            isChannelLocked={isChannelLocked}
            paymentChannel={paymentChannel}
            handlePhoneChange={handlePhoneChange}
            handlePhoneBlur={handlePhoneBlur}
            isVerifying={isVerifying}
            isVerified={isVerified}
            isUnverified={isUnverified}
            handleAccountNumberChange={handleAccountNumberChange}
            handleAccountNumberBlur={handleAccountNumberBlur}
            isBankVerifying={isBankVerifying}
            isBankVerified={isBankVerified}
            isBankUnverified={isBankUnverified}
            banks={banks}
            bankOptions={bankOptions}
            bankFilter={bankFilter}
            isBanksLoading={isBanksLoading}
            isBanksError={isBanksError}
            onRetryBanks={refetchBanks}
            isFormValid={isFormValid}
            disbursementLimits={disbursementLimits}
            isLimitsLoading={isLimitsLoading}
            isLimitExceeded={isLimitExceeded}
          />
          <BatchSummaryCard
            walletBalance={walletBalance}
            isBalanceError={isBalanceError}
            totalDisbursement={totalDisbursement}
            totalRecipients={totalRecipients}
            totalCharges={totalCharges}
            totalPlusFees={totalPlusFees}
          />
        </div>
        <Paper shadow="xs" p={{ base: "sm", sm: "xl" }} mt="md">
          <ManualEntryTable onEdit={handleEditEntry} />
        </Paper>
        <div className="flex justify-end mt-4">
          <div className="bg-white border border-gray-200 rounded-xl px-3 py-1.5">
            {/* Label says "Approval" but POST /bulk auto-approves inline server-side
                (backend "Temp" hack) — there is no separate approval step here. */}
            <CustomButton
              color="primary"
              radius="md"
              disabled={entries.length === 0 || isSubmittingBatch}
              loading={isSubmittingBatch}
              onClick={() =>
                handleSubmitBatch((data) => setSuccessData(data), reset)
              }
            >
              Submit For Approval
            </CustomButton>
          </div>
        </div>
      </div>

      <ManualEntrySuccessModal
        opened={!!successData}
        batchId={successData?.batchId}
        onAddAnother={() => {
          reset({ recipientName: "", phoneNumber: "", bankCode: "", accountNumber: "", amount: "", remarks: "", channel: "" });
          setSuccessData(null);
        }}
        onViewHistory={() => navigate("/bulkpayments")}
      />
    </>
  );
}

export default ManualEntryPage;
